import { useCallback, useRef } from 'react';

/**
 * usePanConstraints Hook
 * 
 * Provides pan constraint logic and pan setters:
 * - constrainPan: Calculates pan limits based on content bounds and zoom
 * - setPan: Constrained pan setter
 * - setPanUnconstrained: Bypasses constraints (for initial positioning)
 * - setPanAnimated: Smoothly animates to constrained position if out of bounds
 */
export function usePanConstraints({
  containerRef,
  containerRectRef,
  tasks,
  rawBoundsRef,
  zoomRef,
  panRef,
  panHook,
  isFocusModeRef,
  zoom, // React state for dependency
  updateDOMTransform,
}) {
  // Animation ref for smooth pan correction
  const panCorrectionAnimRef = useRef(null);

  /**
   * Dynamic pan constraints: allows panning until the outermost node
   * is just barely visible on screen. The limit expands as you zoom in
   * (more freedom) and contracts as you zoom out (less freedom).
   */
  const constrainPan = useCallback((newPan, currentZoom) => {
    // Skip constraints in focus mode - let the focus animation position freely
    if (isFocusModeRef?.current) return newPan;
    
    // EMPTY CANVAS LOCK: When there are no tasks, lock pan to center (0,0)
    // This prevents the "No missions yet" message from appearing off-center
    // and ensures new missions are created in the expected position
    if (tasks.length === 0) return { x: 0, y: 0 };
    
    if (!containerRef.current) return newPan;
    
    // PERFORMANCE: Use cached container rect instead of getBoundingClientRect()
    const container = containerRectRef.current;
    if (container.width === 0) {
      // Fallback if not yet cached (shouldn't happen normally)
      const rect = containerRef.current.getBoundingClientRect();
      containerRectRef.current = { width: rect.width, height: rect.height, left: rect.left, top: rect.top };
    }
    
    const z = currentZoom || zoom;
    const rawBounds = rawBoundsRef.current;
    
    // How much of the content must remain INSIDE the viewport (as % of viewport)
    // This is measured from the viewport edge inward - content can't go past this
    const MIN_VISIBLE_PERCENT = 0.10; // 10% of viewport width/height
    const MIN_VISIBLE_X = container.width * MIN_VISIBLE_PERCENT;
    const MIN_VISIBLE_Y = container.height * MIN_VISIBLE_PERCENT;
    
    let minPanX, maxPanX, minPanY, maxPanY;
    
    // Calculate the pan limits
    // maxPanX: how far RIGHT can we pan? Until leftmost content is MIN_VISIBLE_X from RIGHT edge
    // minPanX: how far LEFT can we pan? Until rightmost content is MIN_VISIBLE_X from LEFT edge
    
    // maxPanX: leftmost content (rawBounds.minX) should be at most (container.width - MIN_VISIBLE_X) from left
    maxPanX = container.width - MIN_VISIBLE_X - rawBounds.minX * z;
    
    // minPanX: rightmost content (rawBounds.maxX) should be at least MIN_VISIBLE_X from left edge
    minPanX = MIN_VISIBLE_X - rawBounds.maxX * z;
    
    // Same logic for Y (using MIN_VISIBLE_Y)
    maxPanY = container.height - MIN_VISIBLE_Y - rawBounds.minY * z;
    minPanY = MIN_VISIBLE_Y - rawBounds.maxY * z;
    
    // CRITICAL: If minPan > maxPan, the content is smaller than viewport minus margins
    // In this case, we need to handle the constraint carefully to avoid sudden snaps.
    // 
    // Instead of forcing to center, we allow the current pan if it keeps content visible,
    // but gently guide towards center when at zoom extremes.
    if (minPanX > maxPanX) {
      // Content fits horizontally - calculate the valid range
      // The "center" is the midpoint, but we allow some slack around it
      const centerPanX = (minPanX + maxPanX) / 2;
      const slack = Math.abs(minPanX - maxPanX) / 2;
      
      // If current pan is within a reasonable range of center, allow it
      // This prevents snapping when zooming smoothly
      if (Math.abs(newPan.x - centerPanX) <= slack + 50) {
        // Allow current position but clamp to a slightly expanded range
        minPanX = centerPanX - slack - 50;
        maxPanX = centerPanX + slack + 50;
      } else {
        // Too far from center - constrain to center
        minPanX = maxPanX = centerPanX;
      }
    }
    if (minPanY > maxPanY) {
      // Content fits vertically - same logic
      const centerPanY = (minPanY + maxPanY) / 2;
      const slack = Math.abs(minPanY - maxPanY) / 2;
      
      if (Math.abs(newPan.y - centerPanY) <= slack + 50) {
        minPanY = centerPanY - slack - 50;
        maxPanY = centerPanY + slack + 50;
      } else {
        minPanY = maxPanY = centerPanY;
      }
    }
    
    return {
      x: Math.max(minPanX, Math.min(maxPanX, newPan.x)),
      y: Math.max(minPanY, Math.min(maxPanY, newPan.y)),
    };
  }, [zoom, tasks.length, containerRef, containerRectRef, rawBoundsRef, isFocusModeRef]);

  // Wrapped setPan that applies constraints
  const setPan = useCallback((newPan) => {
    const resolvedPan = typeof newPan === 'function' ? newPan(panRef.current) : newPan;
    const constrainedPan = constrainPan(resolvedPan, zoomRef.current);
    
    // Update refs
    panRef.current = constrainedPan;
    // Update React state + DOM (panHook.setPan handles DOM update)
    panHook.setPan(constrainedPan);
  }, [panHook, constrainPan, panRef, zoomRef]);
  
  // Unconstrained setPan for initial positioning (bypasses constraints)
  const setPanUnconstrained = useCallback((newPan) => {
    const resolvedPan = typeof newPan === 'function' ? newPan(panRef.current) : newPan;
    
    // Update refs
    panRef.current = resolvedPan;
    // Update React state + DOM (panHook.setPan handles DOM update)
    panHook.setPan(resolvedPan);
  }, [panHook, panRef]);
  
  // Animated setPan - smoothly transitions to constrained position if out of bounds
  const setPanAnimated = useCallback((newPan) => {
    // Cancel any existing animation
    if (panCorrectionAnimRef.current) {
      cancelAnimationFrame(panCorrectionAnimRef.current);
      panCorrectionAnimRef.current = null;
    }
    
    const targetPan = typeof newPan === 'function' ? newPan(panRef.current) : newPan;
    // Use zoomRef.current instead of zoom state - state may be stale during rapid zoom
    const currentZoom = zoomRef.current;
    
    const constrainedPan = constrainPan(targetPan, currentZoom);
    
    // Check if we need to animate (pan is out of bounds)
    const dx = constrainedPan.x - targetPan.x;
    const dy = constrainedPan.y - targetPan.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // If close enough, just snap
    if (distance < 1) {
      panHook.setPan(constrainedPan);
      return;
    }
    
    // Animate smoothly to constrained position
    const startPan = { ...targetPan };
    const startTime = performance.now();
    const duration = Math.min(150, Math.max(80, distance * 0.5)); // 80-150ms based on distance
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(1, elapsed / duration);
      
      // Ease out cubic for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      
      const currentPan = {
        x: startPan.x + (constrainedPan.x - startPan.x) * eased,
        y: startPan.y + (constrainedPan.y - startPan.y) * eased,
      };
      
      // Update ref
      panRef.current = currentPan;
      
      // Update DOM directly for smooth animation (use zoomRef for latest value)
      updateDOMTransform(currentPan, zoomRef.current);
      
      if (progress < 1) {
        panCorrectionAnimRef.current = requestAnimationFrame(animate);
      } else {
        panCorrectionAnimRef.current = null;
        // Only update React state at the end of animation
        panHook.setPan(currentPan, true); // skip DOM update, already done
      }
    };
    
    panCorrectionAnimRef.current = requestAnimationFrame(animate);
  }, [panHook, constrainPan, updateDOMTransform, panRef, zoomRef]);

  return {
    constrainPan,
    setPan,
    setPanUnconstrained,
    setPanAnimated,
  };
}

export default usePanConstraints;

